import type { Insight, Narrative, Quality, Scorecard } from '../api';

export function Notice({
  tone = 'warning',
  message,
  remedy,
}: {
  tone?: 'warning' | 'error' | 'good';
  message: string;
  remedy?: string;
}) {
  return (
    <div className={`notice${tone === 'warning' ? '' : ` ${tone}`}`} role={tone === 'good' ? 'status' : 'alert'}>
      <strong>{message}</strong>
      {remedy !== undefined && remedy !== '' && <div className="remedy">{remedy}</div>}
    </div>
  );
}

export function Scorecards({ cards }: { cards: Scorecard[] }) {
  if (cards.length === 0) return null;

  return (
    <section className="scorecards" aria-label="Headline figures">
      {cards.map((card) => (
        <div className="card scorecard" key={card.id}>
          <div className="label">{card.title}</div>
          <div className="value">{card.formatted}</div>
          <div className="meta">
            {card.trend === null ? (
              // No trend is shown rather than a placeholder 0%. A card that
              // always displays an arrow teaches readers to ignore it.
              card.subtitle
            ) : (
              <>
                <span className={`delta ${card.trend.direction}`}>
                  {card.trend.direction === 'up' ? '↑' : card.trend.direction === 'down' ? '↓' : '→'}{' '}
                  {Math.abs(card.trend.changePercent).toFixed(1)}%
                </span>{' '}
                <span title={card.trend.basis}>{card.subtitle}</span>
              </>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}

export function Insights({ insights }: { insights: Insight[] }) {
  if (insights.length === 0) {
    return (
      <div className="card">
        <header>
          <h2>Findings</h2>
        </header>
        <p className="muted">
          Nothing stood out in this file — no strong correlations, concentrations or outliers.
        </p>
      </div>
    );
  }

  const order = { warning: 0, notable: 1, info: 2 } as const;
  const sorted = [...insights].sort((a, b) => order[a.severity] - order[b.severity]);

  return (
    <div className="card">
      <header>
        <h2>Findings</h2>
        <span className="sub">{insights.length} computed from your data</span>
      </header>
      {sorted.map((insight) => (
        <article className="insight" key={insight.id}>
          <h3>
            {insight.severity === 'warning' && <span className="badge warning">⚠ check</span>}{' '}
            {insight.headline}
          </h3>
          <p>{insight.detail}</p>
          {/* Every figure quoted above, with the statistic that produced it.
              This is what makes a finding checkable rather than assertable. */}
          {insight.evidence.length > 0 && (
            <div className="evidence">
              {insight.evidence.map((e, i) => (
                <span key={i}>
                  {e.label}: <strong>{e.value.toLocaleString('en-US')}</strong> ({e.statistic}
                  {e.column === undefined ? '' : ` of ${e.column}`})
                </span>
              ))}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

export function QualityPanel({ quality }: { quality: Quality }) {
  return (
    <div className="card">
      <header>
        <h2>Data quality</h2>
        <span className="sub">{quality.completeness.toFixed(1)}% of cells populated</span>
      </header>

      <div className="row" style={{ marginBottom: 12 }}>
        <span className="badge">{quality.totalCells.toLocaleString('en-US')} cells</span>
        <span className="badge">{quality.missingCells.toLocaleString('en-US')} missing</span>
        {quality.duplicateRows > 0 && (
          <span className="badge warning">{quality.duplicateRows} duplicate rows</span>
        )}
        {quality.constantColumns.length > 0 && (
          <span className="badge">{quality.constantColumns.length} constant columns</span>
        )}
      </div>

      {quality.issues.length === 0 ? (
        <p className="muted">No structural problems found.</p>
      ) : (
        quality.issues.map((issue, index) => (
          <article className="insight" key={index}>
            <h3>{issue.column === null ? issue.message : `${issue.column} — ${issue.message}`}</h3>
            <p>{issue.remedy}</p>
          </article>
        ))
      )}
    </div>
  );
}

export function NarrativePanel({
  narrative,
  busy,
  onGenerate,
}: {
  narrative: Narrative | null;
  busy: boolean;
  onGenerate: () => void;
}) {
  return (
    <div className="card narrative">
      <header>
        <h2>Summary</h2>
        <button className="ghost" onClick={onGenerate} disabled={busy}>
          {busy ? <span className="spinner" /> : narrative === null ? 'Write summary' : 'Rewrite'}
        </button>
      </header>

      {narrative === null ? (
        <p className="muted">
          Generate a written summary of these findings. Every figure it quotes is checked against the
          analysis before it is shown.
        </p>
      ) : (
        <>
          {narrative.summary.split('\n\n').map((paragraph, index) => (
            <p key={index}>{stripMarkdown(paragraph)}</p>
          ))}

          {narrative.recommendations.length > 0 && (
            <>
              <h3 style={{ fontSize: 13, margin: '14px 0 6px' }}>Next steps</h3>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {narrative.recommendations.map((item, index) => (
                  <li key={index}>{stripMarkdown(item)}</li>
                ))}
              </ul>
            </>
          )}

          {/* Provenance is not a footnote here. A reader deciding how much
              weight to give this text needs to know who wrote it, and whether
              it was written offline because a provider was unavailable. */}
          <div className="provenance">
            {narrative.degraded
              ? 'Written offline — the configured AI provider was unavailable. The figures are unaffected.'
              : narrative.provider === 'deterministic'
                ? 'Composed directly from the computed findings. No AI provider is configured.'
                : `Written by ${narrative.provider}${narrative.model === null ? '' : ` (${narrative.model})`}. Figures were computed by the analytics core, not by the model.`}
          </div>

          {narrative.unverified.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Notice
                message={`${narrative.unverified.length} figure(s) removed from this summary.`}
                remedy={`The model quoted ${narrative.unverified.join(', ')}, which do not appear in the analysis. The sentences containing them were dropped.`}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** The narrators emit light Markdown; this view renders plain text. */
function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .trim();
}
